import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { countAtom1, agentState } from "../App";
import { useRecoilState } from "recoil";
import airportList from "../directories/AirportCode";
import styled, { keyframes } from "styled-components";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { collection, getDocs, addDoc, deleteDoc, updateDoc, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import { enqueueSnackbar } from "notistack";

// MUI Icons only
import FlightIcon        from "@mui/icons-material/Flight";
import HotelIcon         from "@mui/icons-material/Hotel";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import BadgeIcon         from "@mui/icons-material/Badge";
import PeopleIcon        from "@mui/icons-material/People";
import PaymentsIcon      from "@mui/icons-material/Payments";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import CloudUploadIcon   from "@mui/icons-material/CloudUpload";
import DownloadingIcon   from "@mui/icons-material/Downloading";
import AddIcon           from "@mui/icons-material/Add";
import RemoveIcon        from "@mui/icons-material/Remove";

// ─── Constants ────────────────────────────────────────────────────────────────

const TITLES          = ["MR", "MISS", "MSTR", "MRS", "MS"];
const BAGGAGE_OPTIONS = ["Hand Carry Only", "1PC", "2PC", "3PC", "23KG", "25KG", "30KG", "35KG", "40KG", "46KG", "50KG", "20KG"];
const ROOM_TYPES      = ["Single Room", "Double Room", "Triple Room", "Quad Room", "Quint Room", "Family Suite Room", "Ocean Deluxe Room", "Ocean Queen Room", "Deluxe Skyline View, 1 King Bed"];
const BOARD_TYPES     = ["Room Only", "Breakfast", "Half Board", "Full Board", "Sahur Included", "Aftar Included", "Sahur/Aftar Included", "All Inclusive"];
const CAR_TYPES       = ["Saloon Car", "GMC", "Hi-Ace", "Coaster", "King Bus", "H1-Van"];
const VISA_TYPES      = ["E-Wavier Visa","ETA", "Saudi Tourist Visa", "Umrah Visa", "Visit Visa", "Business Visa", "Family Visa"];
const BANKS           = ["Tide", "Natwest", "Monzo", "Allied", "Starlink", "American"];

const DEFAULT_ROW = {
  users:     { title: "", firstName: "", middleName: "", lastName: "" },
  travels:   { date: "", flightNumber: "", from: "", to: "", departure: "", arrival: "", baggage: "" },
  hotels:    { name: "", date: "", checkOutDate: "", numberOfRooms: "", boardType: "", roomType: "" },
  transport: { date: "", carType: "", pickupLocation: "", dropoffLocation: "" },
  visa:      { visaType: "", numberOfVisa: "" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const convertToTime = (timeStr) => {
  const now = new Date();
  now.setHours(parseInt(timeStr.substring(0, 2), 10), parseInt(timeStr.substring(2, 4), 10), 0, 0);
  return now;
};

const customSort = (a, b) => {
  const dateDiff = new Date(a.date) - new Date(b.date);
  if (dateDiff !== 0) return dateDiff;
  return convertToTime(a.departure) - convertToTime(b.departure);
};

// ─── Reusable Input Atoms ─────────────────────────────────────────────────────

const SelectInput = ({ value, onChange, options, placeholder, name, style }) => (
  <select name={name} value={value} onChange={onChange} className="custom-input" style={style}>
    <option value="" disabled>{placeholder}</option>
    {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
  </select>
);

const AirportSelect = ({ value, onChange, name }) => (
  <select name={name} value={value} onChange={onChange} className="custom-input" style={{ minWidth: 180 }}>
    <option value="" disabled>Select Airport</option>
    {airportList.map((a) => (
      <option key={a.id} value={a.code}>{a.name} ({a.code})</option>
    ))}
  </select>
);

// ─── Segment Tables ───────────────────────────────────────────────────────────

const PassengerTable = ({ users, onChange }) => (
  <TableScroll>
    <table className="seg-table">
      <thead><tr>
        <th>Title</th><th>First Name</th><th>Middle Name</th><th>Last Name</th>
      </tr></thead>
      <tbody>
        {users.map((user, i) => (
          <tr key={i}>
            {/* <td><SelectInput value={user.title} onChange={(e) => onChange(e, i, "users")} options={TITLES} placeholder="Title" /></td> */}
           
           <td>
  <SelectInput
    name="title"  
    value={user.title}
    onChange={(e) => onChange(e, i, "users")}
    options={TITLES}
    placeholder="Title"
  />
</td>
            <td><input name="firstName"  value={user.firstName}  onChange={(e) => onChange(e, i, "users")} placeholder="First Name"  className="custom-input" /></td>
            <td><input name="middleName" value={user.middleName} onChange={(e) => onChange(e, i, "users")} placeholder="Middle Name" className="custom-input" /></td>
            <td><input name="lastName"   value={user.lastName}   onChange={(e) => onChange(e, i, "users")} placeholder="Last Name"   className="custom-input" /></td>
          </tr>
        ))}
        {users.length === 0 && <tr><td colSpan={4} className="empty-row">No passengers added yet</td></tr>}
      </tbody>
    </table>
  </TableScroll>
);

const FlightTable = ({ travels, onChange }) => (
  <TableScroll>
    <table className="seg-table">
      <thead><tr>
        <th>Date</th><th>Flight No</th><th>From</th><th>To</th><th>Departure</th><th>Arrival</th><th>Baggage</th>
      </tr></thead>
      <tbody>
        {[...travels].map((travel, i) => (
          <tr key={i}>
            <td><input type="date" name="date" value={travel.date ? formatDate(travel.date) : ""} onChange={(e) => onChange({ target: { name: "date", value: e.target.value } }, i, "travels")} className="custom-input" style={{ minWidth: 140 }} /></td>
            <td><input type="text" name="flightNumber" value={travel.flightNumber} placeholder="EK001" onChange={(e) => onChange({ target: { name: "flightNumber", value: e.target.value.replace(/\s+/g, "").toUpperCase() } }, i, "travels")} className="custom-input" style={{ width: 90 }} /></td>
            <td><AirportSelect name="from" value={travel.from} onChange={(e) => onChange(e, i, "travels")} /></td>
            <td><AirportSelect name="to"   value={travel.to}   onChange={(e) => { const ev = { target: { name: "to", value: e.target.value } }; onChange(ev, i, "travels"); }} /></td>
            <td><input name="departure" value={travel.departure} onChange={(e) => onChange(e, i, "travels")} className="custom-input" style={{ width: 90 }} placeholder="0900" /></td>
            <td><input name="arrival"   value={travel.arrival}   onChange={(e) => onChange(e, i, "travels")} className="custom-input" style={{ width: 90 }} placeholder="1130" /></td>
            <td><SelectInput value={travel.baggage} onChange={(e) => onChange(e, i, "travels")} options={BAGGAGE_OPTIONS} placeholder="Baggage" style={{ minWidth: 130 }} /></td>
          </tr>
        ))}
        {travels.length === 0 && <tr><td colSpan={7} className="empty-row">No flights added yet</td></tr>}
      </tbody>
    </table>
  </TableScroll>
);

const HotelTable = ({ hotels, onChange }) => (
  <TableScroll>
    <table className="seg-table">
      <thead><tr>
        <th>Hotel Name</th><th>Check-in</th><th>Check-out</th><th>Rooms</th><th>Room Type</th><th>Board Type</th>
      </tr></thead>
      <tbody>
        {hotels.map((hotel, i) => (
          <tr key={i}>
            <td><input name="name" value={hotel.name} onChange={(e) => onChange(e, i, "hotels")} placeholder="Hotel Name" className="custom-input" style={{ minWidth: 180 }} /></td>
            <td><input type="date" name="date" value={hotel.date ? formatDate(hotel.date) : ""} onChange={(e) => onChange(e, i, "hotels")} className="custom-input" style={{ minWidth: 140 }} /></td>
            <td><input type="date" name="checkOutDate" value={hotel.checkOutDate ? formatDate(hotel.checkOutDate) : ""} onChange={(e) => onChange(e, i, "hotels")} className="custom-input" style={{ minWidth: 140 }} /></td>
            <td><input type="number" name="numberOfRooms" value={hotel.numberOfRooms} onChange={(e) => onChange(e, i, "hotels")} className="custom-input" style={{ width: 70 }} /></td>
            <td><SelectInput value={hotel.roomType}  onChange={(e) => onChange(e, i, "hotels")} options={ROOM_TYPES}  placeholder="Room Type"  style={{ minWidth: 160 }} /></td>
            <td><SelectInput value={hotel.boardType} onChange={(e) => onChange(e, i, "hotels")} options={BOARD_TYPES} placeholder="Board Type" style={{ minWidth: 160 }} /></td>
          </tr>
        ))}
        {hotels.length === 0 && <tr><td colSpan={6} className="empty-row">No hotels added yet</td></tr>}
      </tbody>
    </table>
  </TableScroll>
);

const TransportTable = ({ transport, onChange }) => (
  <TableScroll>
    <table className="seg-table">
      <thead><tr>
        <th>Date</th><th>Car Type</th><th>Pickup</th><th>Dropoff</th>
      </tr></thead>
      <tbody>
        {transport.map((trans, i) => (
          <tr key={i}>
            <td><input type="date" name="date" value={trans.date ? formatDate(trans.date) : ""} onChange={(e) => onChange(e, i, "transport")} className="custom-input" style={{ minWidth: 140 }} /></td>
            <td><SelectInput value={trans.carType} onChange={(e) => onChange(e, i, "transport")} options={CAR_TYPES} placeholder="Car Type" style={{ minWidth: 130 }} /></td>
            <td><input name="pickupLocation"  value={trans.pickupLocation}  onChange={(e) => onChange(e, i, "transport")} className="custom-input" placeholder="Pickup location" /></td>
            <td><input name="dropoffLocation" value={trans.dropoffLocation} onChange={(e) => onChange(e, i, "transport")} className="custom-input" placeholder="Dropoff location" /></td>
          </tr>
        ))}
        {transport.length === 0 && <tr><td colSpan={4} className="empty-row">No transport added yet</td></tr>}
      </tbody>
    </table>
  </TableScroll>
);

const VisaTable = ({ visa, onChange }) => (
  <TableScroll>
    <table className="seg-table">
      <thead><tr>
        <th>Visa Type</th><th>Number of Visas</th>
      </tr></thead>
      <tbody>
        {visa.map((visaItem, i) => (
          <tr key={i}>
            <td><SelectInput value={visaItem.visaType} onChange={(e) => onChange(e, i, "visa")} options={VISA_TYPES} placeholder="Visa Type" style={{ minWidth: 200 }} /></td>
            <td><input type="number" name="numberOfVisa" value={visaItem.numberOfVisa} onChange={(e) => onChange(e, i, "visa")} className="custom-input" style={{ width: 100 }} /></td>
          </tr>
        ))}
        {visa.length === 0 && <tr><td colSpan={2} className="empty-row">No visas added yet</td></tr>}
      </tbody>
    </table>
  </TableScroll>
);

// ─── Segment Card Wrapper ─────────────────────────────────────────────────────

const SegmentCard = ({ icon, title, accent, children, onAdd, addLabel, onRemove, removeLabel }) => (
  <Card>
    <CardHeader accent={accent}>
      <CardHeaderLeft>
        <CardIconWrap accent={accent}>{icon}</CardIconWrap>
        <span>{title}</span>
      </CardHeaderLeft>
      <CardHeaderRight>
        <AddBtn onClick={onAdd}>
          <AddIcon style={{ fontSize: 15 }} /> {addLabel}
        </AddBtn>
        <RemoveBtn onClick={onRemove}>
          <RemoveIcon style={{ fontSize: 15 }} /> {removeLabel}
        </RemoveBtn>
      </CardHeaderRight>
    </CardHeader>
    <CardBody>{children}</CardBody>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────

function EditInvoice() {
  const [state, setState] = useRecoilState(countAtom1);
  const [agents]          = useRecoilState(agentState);
  const navigate          = useNavigate();

  // ── Local state for computed total paid from bankPayments ──
  const [computedTotalPaid, setComputedTotalPaid] = useState(null);
  const [loadingPayments,   setLoadingPayments]   = useState(false);

  const { users, travels, hotels, transport, visa, ourReference, agentsName, totalQuote, totalPaid, phoneNumber, emailAddress, bank } = state;

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      travels:      prev.travels.length      ? prev.travels      : [],
      users:        prev.users.length        ? prev.users        : [],
      hotels:       prev.hotels.length       ? prev.hotels       : [],
      transport:    prev.transport.length    ? prev.transport    : [],
      visa:         prev.visa.length         ? prev.visa         : [],
      ourReference: prev.ourReference || 0,
      agentsName:   prev.agentsName   || "",
      totalQuote:   prev.totalQuote   || 0,
      totalPaid:    prev.totalPaid    || 0,
      emailAddress: prev.emailAddress || "",
      phoneNumber:  prev.phoneNumber  || "",
      bank:         prev.bank         || "",
    }));
  }, [setState]);

  useEffect(() => {
    if (state.users[0]?.firstName          === "") handleRemoveRow("users");
    if (state.travels[0]?.departure        === "") handleRemoveRow("travels");
    if (state.hotels[0]?.name              === "") handleRemoveRow("hotels");
    if (state.visa[0]?.numberOfVisas       === "") handleRemoveRow("visa");
    if (state.transport[0]?.pickupLocation === "") handleRemoveRow("transport");
  }, []);

  // ── Fetch bankPayments total whenever ourReference changes ──────────────────
  function PopulatePaid () {
    const folderNo = Number(ourReference);
    if (!folderNo) {
      setComputedTotalPaid(null);
      return;
    }

    const fetchPayments = async () => {
      setLoadingPayments(true);
      try {
        const snap = await getDocs(
          query(collection(db, "bankPayments"), where("folderNo", "==", String(folderNo)))
        );

        let total = 0;
        snap.docs.forEach(doc => {
          const p        = doc.data();
          const currency = (p.currency || "").toUpperCase();
          if      (currency === "GBP") total += Number(p.paymentGBP) || 0;
          else if (currency === "PKR") total += Number(p.paymentPKR) || 0;
          else if (currency === "USD") total += Number(p.paymentUSD) || 0;
        });

        setComputedTotalPaid(total);
      } catch (err) {
        console.error("Error fetching bankPayments:", err);
        setComputedTotalPaid(null);
      } finally {
        setLoadingPayments(false);
      }
    };

    fetchPayments();
  }

  const updateState    = (key, value) => setState((prev) => ({ ...prev, [key]: value }));
  const handleChange   = (e, index, category) => {
    const { name, value } = e.target;
    const updated = [...state[category]];
    updated[index] = { ...updated[index], [name]: value };
    updateState(category, updated);
  };
  const handleAddRow    = (category) => updateState(category, [...state[category], DEFAULT_ROW[category]]);
  const handleRemoveRow = (category) => updateState(category, state[category].slice(0, -1));

  const clearAndUpload = async (collectionName, docs) => {
    const ref  = collection(db, collectionName);
    const snap = await getDocs(query(ref, where("FolderNo", "==", Number(ourReference))));
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    for (const data of docs) await addDoc(ref, { FolderNo: Number(ourReference), ...data });
  };

  const uploadData = async () => {
    try {
      const folderRef  = collection(db, "FOLDERS");
      const folderSnap = await getDocs(query(folderRef, where("FolderNo", "==", Number(ourReference))));
      await Promise.all(folderSnap.docs.map((d) =>
        updateDoc(d.ref, { Agent: agentsName, TotalQoute: totalQuote, phoneNumber, emailAddress, bank })
        // Note: TotalPaid is intentionally NOT saved here — it's derived from bankPayments
      ));
      const fmtDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
      await clearAndUpload("FlightSegments",    travels.map((t) => ({ date: fmtDate(t.date), flightNumber: t.flightNumber, from: t.from, to: t.to, departure: t.departure, arrival: t.arrival, baggage: t.baggage })));
      await clearAndUpload("PassengerSegments", users.map((u) => ({ firstName: u.firstName, lastName: u.lastName, middleName: u.middleName, title: u.title })));
      await clearAndUpload("HotelSegments",     hotels.map((h) => ({ boardType: h.boardType, checkOutDate: h.checkOutDate, date: h.date, name: h.name, numberOfRooms: h.numberOfRooms, roomType: h.roomType })));
      await clearAndUpload("TransportSegments", transport.map((t) => ({ carType: t.carType, date: t.date, dropoffLocation: t.dropoffLocation, pickupLocation: t.pickupLocation })));
      await clearAndUpload("VisaSegments",      visa.map((v) => ({ numberOfVisa: v.numberOfVisa, visaType: v.visaType })));
      enqueueSnackbar("Folder updated successfully!", { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating folder.", { variant: "error" });
    }
  };

  const fetchFolderData = async () => {
    try {


      
      const fetchCol = async (colName) => {
        const snap = await getDocs(query(collection(db, colName), where("FolderNo", "==", Number(ourReference))));
        return snap.docs.map((d) => d.data());
      };
      const [travelsData, passengersData, folderData, hotelData, transportData, visaData] = await Promise.all([
        fetchCol("FlightSegments"), fetchCol("PassengerSegments"), fetchCol("FOLDERS"),
        fetchCol("HotelSegments"),  fetchCol("TransportSegments"),  fetchCol("VisaSegments"),
      ]);
      if (!folderData.length) { enqueueSnackbar("Folder not found!", { variant: "error" });}
      if (!travelsData.length) { enqueueSnackbar("Flight Segments not found!", { variant: "error" });}
      if (!passengersData.length) { enqueueSnackbar("Passenger Segments not found!", { variant: "error" });}
      if (!hotelData.length) { enqueueSnackbar("Hotel Segments not found!", { variant: "error" });}
      if (!transportData.length) { enqueueSnackbar("Transport Segments not found!", { variant: "error" });}
      if (!visaData.length) { enqueueSnackbar("Visa Segments not found!", { variant: "error" });}
      const folder = folderData[0];
      setState((prev) => ({
        ...prev, travels: travelsData.sort(customSort), users: passengersData,
        hotels: hotelData, transport: transportData, visa: visaData,
        agentsName: folder.Agent,
        // totalPaid intentionally not set from FOLDERS — computed from bankPayments instead
        totalQuote: folder.TotalQoute, phoneNumber: folder.phoneNumber,
        emailAddress: folder.emailAddress, bank: folder.bank,
      }));

      PopulatePaid();
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Invalid folder number.", { variant: "error" });
    }
  };

  const handleSubmit = (e) => {
    if (ourReference !== 0 && agentsName && totalQuote !== 0) {
      e.preventDefault();
      navigate("/hero", { state: { users, travels, hotels, transport, visa, ourReference, agentsName, totalQuote, totalPaid: computedTotalPaid ?? 0, bank } });
    } else {
      enqueueSnackbar("Missing data for invoice!", { variant: "error" });
    }
  };

  // ── Derived display value for total paid ──
  const displayTotalPaid = loadingPayments
    ? "Loading…"
    : computedTotalPaid != null
      ? computedTotalPaid.toLocaleString()
      : "—";

  return (
    <Wrapper>
      <PageTitle>Edit Invoice</PageTitle>

      {/* ── Payment & Reference ── */}
      <Card>
        <CardHeader accent="#2563eb">
          <CardHeaderLeft>
            <CardIconWrap accent="#2563eb"><PaymentsIcon style={{ fontSize: 18 }} /></CardIconWrap>
            <span>Payment &amp; Reference</span>
          </CardHeaderLeft>
          <FetchBtn onClick={() => { if (window.confirm("This will overwrite current unsaved data. Continue?")) fetchFolderData(); }}>
            <DownloadingIcon style={{ fontSize: 16 }} /> Fetch Data
          </FetchBtn>
        </CardHeader>

        <CardBody>
          <FieldGrid>
            <Field>
              <label>Folder Number</label>
              <input type="text" value={ourReference} onChange={(e) => updateState("ourReference", e.target.value)} className="custom-input" placeholder="e.g. 1042" />
            </Field>
            <Field>
              <label>Agent</label>
              <select value={agentsName} onChange={(e) => updateState("agentsName", e.target.value)} className="custom-input">
                <option value="" disabled>Select Agent</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.id}</option>)}
              </select>
            </Field>
            <Field>
              <label>Bank</label>
              <SelectInput value={bank} onChange={(e) => updateState("bank", e.target.value)} options={BANKS} placeholder="Select Bank" />
            </Field>
            <Field>

             

              <label>Total Quote (
    {bank === "Allied" && "PKR"}
    {bank === "Tide" && "£"}
    {bank === "Natwest" && "£"}
    {bank === "Monzo" && "£"}
    {bank === "Starlink" && "£"}
     {bank === "Starling" && "£"}
    {bank === "American" && "$"})</label>
              <input type="number" value={totalQuote} onChange={(e) => updateState("totalQuote", e.target.value)} className="custom-input" placeholder="0.00" />
            </Field>

            {/* ── Total Paid — read-only, sourced from bankPayments ── */}
            <Field>
              <label>Total Paid</label>
              <ReadOnlyPaidBox $loading={loadingPayments}>
                <PaidValue>{displayTotalPaid}</PaidValue>
                <PaidBadge>Auto</PaidBadge>
              </ReadOnlyPaidBox>
              <ReadOnlyHint>Calculated from bank payments — not editable</ReadOnlyHint>
            </Field>

            <Field>
              <label>Email Address</label>
              <input type="email" value={emailAddress} onChange={(e) => updateState("emailAddress", e.target.value)} className="custom-input" placeholder="client@email.com" />
            </Field>
            <Field>
              <label>Phone Number</label>
              <PhoneInput
                country="gb"
                value={phoneNumber}
                onChange={(val) => updateState("phoneNumber", val)}
                inputStyle={{ width: "100%", height: "44px", paddingLeft: "50px", borderRadius: "6px", border: "2px solid #ccc", fontSize: "14px", outline: "none" }}
                buttonStyle={{ border: "none", background: "transparent" }}
              />
            </Field>
          </FieldGrid>

          {/* Balance indicator */}
          {(totalQuote > 0 || computedTotalPaid > 0) && (
            <BalanceBar>
              <BalanceItem>
                <BalanceLabel>Quote</BalanceLabel>
             <BalanceVal color="#2563eb">
    {bank === "Allied" && "PKR"}
    {bank === "Tide" && "£"}
    {bank === "Natwest" && "£"}
    {bank === "Monzo" && "£"}
    {bank === "Starlink" && "£"}
     {bank === "Starling" && "£"}
    {bank === "American" && "$"}
    {parseFloat(totalQuote || 0).toFixed(2)}
  </BalanceVal>
              </BalanceItem>
              <BalanceDivider />
              <BalanceItem>
                <BalanceLabel>Paid</BalanceLabel>
                <BalanceVal color="#059669">
   {bank === "Allied" && "PKR"}
    {bank === "Tide" && "£"}
    {bank === "Natwest" && "£"}
    {bank === "Monzo" && "£"}
    {bank === "Starlink" && "£"}
     {bank === "Starling" && "£"}
    {bank === "American" && "$"}
                  {loadingPayments ? "…" : `${parseFloat(computedTotalPaid || 0).toFixed(2)}`}
                </BalanceVal>
              </BalanceItem>
              <BalanceDivider />
              <BalanceItem>
                <BalanceLabel>Remaining</BalanceLabel>
                <BalanceVal color={(totalQuote - (computedTotalPaid || 0)) > 0 ? "#dc2626" : "#059669"}>
    {bank === "Allied" && "PKR"}
    {bank === "Tide" && "£"}
    {bank === "American" && "$"}
                  {loadingPayments ? "…" : `${(parseFloat(totalQuote || 0) - parseFloat(computedTotalPaid || 0)).toFixed(2)}`}
                </BalanceVal>
              </BalanceItem>
            </BalanceBar>
          )}
        </CardBody>
      </Card>

      {/* ── Passengers ── */}
      <SegmentCard
        icon={<PeopleIcon style={{ fontSize: 18 }} />}
        title="Passenger Segments"
        accent="#7c3aed"
        onAdd={() => handleAddRow("users")}      addLabel="Add Passenger"
        onRemove={() => handleRemoveRow("users")} removeLabel="Remove"
      >
        <PassengerTable users={users} onChange={handleChange} />
      </SegmentCard>

      {/* ── Flights ── */}
      <SegmentCard
        icon={<FlightIcon style={{ fontSize: 18 }} />}
        title="Flight Segments"
        accent="#0891b2"
        onAdd={() => handleAddRow("travels")}      addLabel="Add Flight"
        onRemove={() => handleRemoveRow("travels")} removeLabel="Remove"
      >
        <FlightTable travels={travels} onChange={handleChange} />
      </SegmentCard>

      {/* ── Hotels ── */}
      <SegmentCard
        icon={<HotelIcon style={{ fontSize: 18 }} />}
        title="Hotel Segments"
        accent="#7c3aed"
        onAdd={() => handleAddRow("hotels")}      addLabel="Add Hotel"
        onRemove={() => handleRemoveRow("hotels")} removeLabel="Remove"
      >
        <HotelTable hotels={hotels} onChange={handleChange} />
      </SegmentCard>

      {/* ── Transport ── */}
      <SegmentCard
        icon={<DirectionsBusIcon style={{ fontSize: 18 }} />}
        title="Transport Segments"
        accent="#059669"
        onAdd={() => handleAddRow("transport")}      addLabel="Add Transport"
        onRemove={() => handleRemoveRow("transport")} removeLabel="Remove"
      >
        <TransportTable transport={transport} onChange={handleChange} />
      </SegmentCard>

      {/* ── Visa ── */}
      <SegmentCard
        icon={<BadgeIcon style={{ fontSize: 18 }} />}
        title="Visa Segments"
        accent="#d97706"
        onAdd={() => handleAddRow("visa")}      addLabel="Add Visa"
        onRemove={() => handleRemoveRow("visa")} removeLabel="Remove"
      >
        <VisaTable visa={visa} onChange={handleChange} />
      </SegmentCard>

      {/* ── Final Actions ── */}
      <FinalActions>
        <PreviewBtn onClick={handleSubmit}>
          <VisibilityIcon style={{ fontSize: 17 }} /> Preview Invoice
        </PreviewBtn>
        <UpdateBtn onClick={uploadData}>
          <CloudUploadIcon style={{ fontSize: 17 }} /> Update Folder
        </UpdateBtn>
      </FinalActions>
    </Wrapper>
  );
}

// ─── Styled Components ────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; }`;

const Wrapper = styled.section`
  padding: 0 24px 80px;
  margin-top: 30px;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  animation: ${fadeIn} 0.3s ease;

  .custom-input {
    padding: 9px 12px; font-size: 14px;
    border: 2px solid #e2e8f0; border-radius: 6px;
    outline: none; width: 100%; box-sizing: border-box;
    background: white; transition: border-color 0.2s, box-shadow 0.2s;
    color: #1e293b;
    &:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
    &::placeholder { color: #94a3b8; font-style: italic; }
  }
`;

const PageTitle = styled.h2`
  font-size: 28px; font-weight: 700; color: #2563eb;
  background: #e0f2fe; display: inline-block;
  padding: 8px 20px; border-radius: 8px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08); letter-spacing: 0.5px;
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: white;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 13px 20px;
  background: white;
  border-left: 4px solid ${p => p.accent || "#2563eb"};
  border-bottom: 0.5px solid #e2e8f0;
`;

const CardHeaderLeft = styled.div`
  display: flex; align-items: center; gap: 10px;
  font-size: 15px; font-weight: 700; color: #1e293b;
`;

const CardHeaderRight = styled.div`display: flex; align-items: center; gap: 8px;`;

const CardIconWrap = styled.div`
  width: 30px; height: 30px; border-radius: 6px;
  background: ${p => p.accent}18; color: ${p => p.accent};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;

const CardBody = styled.div`padding: 20px 24px;`;

// ─── Fields ───────────────────────────────────────────────────────────────────

const FieldGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 16px;
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  min-width: 200px; flex: 1;
  label {
    font-size: 11px; font-weight: 700; color: #64748b;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
`;

// ─── Read-only Total Paid ─────────────────────────────────────────────────────

const ReadOnlyPaidBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  min-height: 18px;
  opacity: ${p => p.$loading ? 0.6 : 1};
  transition: opacity 0.2s;
`;

const PaidValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #059669;
  font-variant-numeric: tabular-nums;
`;

const PaidBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #059669;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  padding: 2px 6px;
  border-radius: 4px;
`;

const ReadOnlyHint = styled.span`
  font-size: 10.5px;
  color: #94a3b8;
  font-style: italic;
  margin-top: -2px;
`;

// ─── Balance Bar ──────────────────────────────────────────────────────────────

const BalanceBar = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  margin-top: 20px;
  background: #f8fafc; border: 0.5px solid #e2e8f0;
  border-radius: 10px; padding: 14px 20px;
`;
const BalanceItem  = styled.div`display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 100px;`;
const BalanceLabel = styled.div`font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; color: #94a3b8;`;
const BalanceVal   = styled.div`font-size: 20px; font-weight: 700; color: ${p => p.color}; font-variant-numeric: tabular-nums;`;
const BalanceDivider = styled.div`width: 1px; height: 36px; background: #e2e8f0; margin: 0 8px;`;

// ─── Segment Tables ───────────────────────────────────────────────────────────

const TableScroll = styled.div`
  overflow-x: auto;

  table.seg-table {
    width: 100%; border-collapse: collapse; font-size: 13px;

    thead tr { background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
    th {
      padding: 9px 12px; text-align: left;
      font-size: 11px; font-weight: 600; color: #94a3b8;
      text-transform: uppercase; letter-spacing: 0.6px; white-space: nowrap;
    }
    td {
      padding: 7px 6px;
      border-bottom: 0.5px solid #f1f5f9;
      vertical-align: middle;
    }
    tr:last-child td { border-bottom: none; }
    tr:hover td { background: #f8fafc; }

    .empty-row {
      text-align: center; padding: 24px;
      color: #94a3b8; font-style: italic; font-size: 13px;
    }
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────

const AddBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 6px 12px; background: #2563eb; color: white;
  border: none; border-radius: 6px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: background 0.2s;
  &:hover { background: #1d4ed8; }
`;

const RemoveBtn = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 6px 12px; background: #fef2f2; color: #dc2626;
  border: 1px solid #fca5a5; border-radius: 6px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: #dc2626; color: white; border-color: #dc2626; }
`;

const FetchBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; background: #024059; color: white;
  border: none; border-radius: 6px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background 0.2s;
  &:hover { background: #013044; }
`;

const FinalActions = styled.div`
  display: flex; gap: 12px; margin-top: 8px; justify-content: flex-end;
`;

const PreviewBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 12px 24px; background: #7c3aed; color: white;
  border: none; border-radius: 8px; font-size: 15px; font-weight: 600;
  cursor: pointer; transition: background 0.2s, transform 0.15s;
  &:hover { background: #6d28d9; transform: translateY(-1px); }
`;

const UpdateBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 12px 24px; background: #28a745; color: white;
  border: none; border-radius: 8px; font-size: 15px; font-weight: 600;
  cursor: pointer; transition: background 0.2s, transform 0.15s;
  &:hover { background: #218838; transform: translateY(-1px); }
`;

export default EditInvoice;